import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { AttachmentsField, type AttachmentRef } from '@/components/composites/attachments-field'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { Card } from '@/types/entities'
import { stopWorkIfOn } from '@/features/worktime'
import { cardsApi } from './api'

/**
 * Finishing work is the moment somebody has a screenshot or a photo of it in hand — and a
 * completed item is frozen, so afterwards there is nowhere to put it. This asks once, with the
 * drop zone already listening for a paste, and costs one click when there is nothing to add.
 *
 * Photos go onto the progress update, which is what the approver and everybody reading the
 * item afterwards sees as the evidence; the stage change follows once they are saved.
 */
export function CompleteWorkDialog({
  card,
  open,
  onOpenChange,
  onCompleted,
}: {
  card: Card
  open: boolean
  onOpenChange: (open: boolean) => void
  onCompleted?: (updated: Card) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The body mounts only while open, so each opening starts from the item as it is now. */}
      <CompleteBody card={card} onOpenChange={onOpenChange} onCompleted={onCompleted} />
    </Dialog>
  )
}

function CompleteBody({
  card,
  onOpenChange,
  onCompleted,
}: {
  card: Card
  onOpenChange: (open: boolean) => void
  onCompleted?: (updated: Card) => void
}) {
  const queryClient = useQueryClient()
  const existing = card.progressAttachments ?? []
  const [refs, setRefs] = useState<AttachmentRef[]>(() => existing.map((a) => ({ key: a.key, name: a.name })))
  const [uploading, setUploading] = useState(false)

  const finish = useMutation({
    mutationFn: async () => {
      const unchanged = refs.length === existing.length && refs.every((r) => existing.some((a) => a.key === r.key))
      if (!unchanged) await cardsApi.patch(card.id, { progressAttachments: refs })
      return cardsApi.patch(card.id, { status: 'done' })
    },
    onSuccess: (updated) => {
      toast.success('Marked done', { description: card.title })
      void stopWorkIfOn(queryClient, card.id)
      queryClient.setQueryData(qk.card(card.id), updated)
      void queryClient.invalidateQueries({ queryKey: qk.cardsPrefix(card.boardId) })
      onCompleted?.(updated)
      onOpenChange(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  return (
    <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <BadgeCheck className="size-4 text-success" /> Mark done
        </DialogTitle>
        <DialogDescription>
          “{card.title}” — add photos of the finished work if you have them. Once it is done the item is locked.
        </DialogDescription>
      </DialogHeader>

      <AttachmentsField
        label="Photos"
        imagesOnly
        listenForPaste
        existing={existing}
        onChange={setRefs}
        onBusyChange={setUploading}
        disabled={finish.isPending}
      />

      <DialogFooter>
        <Button type="button" variant="outline" disabled={finish.isPending} onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="button" disabled={finish.isPending || uploading} onClick={() => finish.mutate()}>
          {finish.isPending ? 'Saving…' : uploading ? 'Uploading…' : 'Mark done'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
